import { h, render } from "preact";
import { App } from "./App";
import { createApi, type WidgetConfig } from "./api";
import { Popup } from "./Popup";
import { styles } from "./styles/index";
import { applyAppearance, attrsAppearance } from "./theme";

/**
 * <workky-agent data-workky-key="pk_…" [data-workky-api] [data-workky-mode="inline|popup"]
 *               [data-workky-theme="auto|light|dark"] [data-workky-accent="#hex"] [data-workky-position="right|left"]
 *               [data-workky-label="Talk to us"] [data-workky-open]>
 * Renders in a closed shadow root. Appearance = tenant config (fetched) overridden by the attributes above and by a
 * host CSS custom property `--workky-accent` on the element.
 */
export class WorkkyAgentElement extends HTMLElement {
  private root: ShadowRoot | undefined;

  connectedCallback(): void {
    if (this.root) return;
    const embedKey = this.dataset.workkyKey;
    const apiBase = this.dataset.workkyApi ?? scriptOrigin();
    if (!embedKey || !apiBase) {
      console.error("[workky] missing data-workky-key or data-workky-api");
      return;
    }
    this.root = this.attachShadow({ mode: "closed" });
    attachStyles(this.root);
    const mount = document.createElement("div");
    this.root.append(mount);
    const attrs = attrsAppearance(this.dataset);
    applyAppearance(this, {}, attrs);
    const api = createApi(apiBase, embedKey);
    const draw = (config: WidgetConfig) => {
      const merged: WidgetConfig = { tenantName: config.tenantName, appearance: { ...config.appearance, ...stripUndef(attrs) } };
      applyAppearance(this, config.appearance ?? {}, attrs);
      const props = { apiBase, embedKey, config: merged };
      render(this.dataset.workkyMode === "popup" ? h(Popup, { ...props, openOnLoad: this.dataset.workkyOpen !== undefined }) : h(App, props), mount);
    };
    draw({});
    void api.ping();
    void api.config().then(draw);
  }

  disconnectedCallback(): void {
    if (!this.root) return;
    render(null, this.root.lastElementChild as HTMLElement);
    this.root = undefined;
  }
}

let sheet: CSSStyleSheet | undefined;
function attachStyles(root: ShadowRoot): void {
  if ("adoptedStyleSheets" in Document.prototype && typeof CSSStyleSheet !== "undefined" && "replaceSync" in CSSStyleSheet.prototype) {
    if (!sheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(styles);
    }
    root.adoptedStyleSheets = [sheet];
    return;
  }
  const style = document.createElement("style");
  style.textContent = styles;
  root.append(style);
}

function stripUndef<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

/** Origin of the script that loaded us, used as the default API base. */
function scriptOrigin(): string | undefined {
  const src = (document.currentScript as HTMLScriptElement | null)?.src ?? document.querySelector<HTMLScriptElement>('script[src*="widget.js"]')?.src;
  try {
    return src ? new URL(src).origin : undefined;
  } catch {
    return undefined;
  }
}
